/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import React, { ReactElement } from 'react';
import { SectionRow } from 'templates/pubSub/types/TemplateType';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import SectionElementContent from './SectionElementContent';
import SectionElementPlaceholder from './SectionElementPlaceholder';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';
import { usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';
import RenderOnCondition from './RenderOnCondition';

interface ISectionRowContentProps {
  templateContext: TemplateContext;
  row: SectionRow;
  cols: string;
  startingNodeId?: NodeIdentifier;
}

export default function SectionRowContent(props: ISectionRowContentProps): ReactElement | null {
  const validationNodePath = TemplateHelperPubSub.resolveRowValidationNodePath(props.row);
  const validationNodeFilters = TemplateHelperPubSub.createFilter(props.templateContext.namespaces, validationNodePath);
  const validationNode = usePubSubNode(validationNodeFilters, props.startingNodeId);

  if (TemplateHelperPubSub.shouldHideOptionalRow(validationNode, props.row, `SectionRowContent:${props.row.id}`)) {
    return null;
  }

  const filterTitle = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.row.titleVariable?.variablePath,
  );
  const nodeTitle = usePubSubNode(filterTitle, props.startingNodeId);
  const valueTitle = usePubSubValue(nodeTitle);

  const title = valueTitle !== undefined ? valueTitle : props.row.title;

  return (
    <div className="section">
      {props.row.title && (
        <div className="row">
          <div className="col">
            <h4 className="h5">{title}</h4>
          </div>
        </div>
      )}
      <div className="row">
        {props.row.elements.map((element, elementIndex) => (
          <RenderOnCondition
            key={TemplateHelperPubSub.getPlaceholderKey(element.id, elementIndex)}
            templateContext={props.templateContext}
            condition={element.condition}
            startingNodeId={props.startingNodeId}
          >
            {!element.placeholders || element.placeholders.length === 0 ? (
              <SectionElementContent
                templateContext={props.templateContext}
                cols={props.cols}
                element={element}
                startingNodeId={props.startingNodeId}
              />
            ) : (
              <SectionElementPlaceholder
                templateContext={props.templateContext}
                cols={props.cols}
                element={element}
                placeholder={element.placeholders[0]}
                placeholderIndex={0}
                startingNodeId={props.startingNodeId}
              />
            )}
          </RenderOnCondition>
        ))}
      </div>
    </div>
  );
}
